import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { PublicOrganizer } from '@radevents/shared';
import BundeslandOptions from '../BundeslandOptions';
import organizerService from '../../services/organizerService';

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '10px 12px', borderRadius: 10, fontSize: 14,
  background: 'var(--input-bg)', border: '1px solid var(--input-border)', color: 'var(--text-primary)',
};

const emptyProposal = {
  proposedName: '',
  proposedWebsite: '',
  proposedLogoUrl: '',
  proposedStreet: '',
  proposedPostalCode: '',
  proposedCity: '',
  proposedBundesland: '',
  proposedImpressumUrl: '',
  proposedContactPerson: '',
  proposedEmail: '',
  proposedPhone: '',
  proposedStravaClubUrl: '',
  proposedInstagramUrl: '',
  proposedFacebookUrl: '',
  proposedKomootUrl: '',
  submitterName: '',
  submitterEmail: '',
  note: '',
};

interface Props {
  show: boolean;
  /** Der Veranstalter, dessen Angaben vorbelegt werden. */
  organizer: PublicOrganizer;
  onClose: () => void;
}

/**
 * „Änderung vorschlagen" für Besucher der Veranstalterseite.
 *
 * Eigene Datei, weil der Dialog nichts von der Seite braucht außer dem Veranstalter selbst — und
 * weil er mit Formularzustand, Vorbelegung und Absenden rund 230 Zeilen mitbringt. In
 * `OrganizerDetail` lagen die verteilt zwischen Kartenlink, Antragsformular und Briefkopf.
 *
 * Der Inhalt ist unverändert. Nicht zu verwechseln mit dem Bearbeiten im Briefkopf: das ist der
 * Weg für den Verein selbst ([OrganizerLetterhead]), dieser hier der für alle anderen.
 */
const OrganizerProposeDialog: React.FC<Props> = ({ show, organizer, onClose }) => {
  const [proposal, setProposal] = useState(emptyProposal);
  const [submitting, setSubmitting] = useState(false);
  const organizerId = organizer.id;

  useEffect(() => {
    if (!show) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.body.classList.add('modal-open');
    return () => {
      document.body.style.overflow = prev;
      document.body.classList.remove('modal-open');
    };
  }, [show]);

  // Vorbelegen, sobald der Dialog aufgeht: der Vorschlag geht von dem aus, was heute dasteht.
  useEffect(() => {
    if (!show) return;
    setProposal({
      ...emptyProposal,
      proposedName: organizer?.name ?? '',
      proposedWebsite: organizer?.website ?? '',
      proposedStreet: organizer?.street ?? '',
      proposedPostalCode: organizer?.postalCode ?? '',
      proposedCity: organizer?.city ?? '',
      proposedBundesland: organizer?.bundesland ?? '',
      proposedStravaClubUrl: organizer?.stravaClubUrl ?? '',
      proposedInstagramUrl: organizer?.instagramUrl ?? '',
      proposedFacebookUrl: organizer?.facebookUrl ?? '',
      proposedKomootUrl: organizer?.komootUrl ?? '',
    });
  }, [show, organizer]);

  const submit = async () => {
    setSubmitting(true);
    try {
      await organizerService.proposeEdit(organizerId, {
        proposedName: proposal.proposedName || undefined,
        proposedWebsite: proposal.proposedWebsite || undefined,
        proposedLogoUrl: proposal.proposedLogoUrl || undefined,
        proposedStreet: proposal.proposedStreet || undefined,
        proposedPostalCode: proposal.proposedPostalCode || undefined,
        proposedCity: proposal.proposedCity || undefined,
        proposedBundesland: proposal.proposedBundesland || undefined,
        proposedImpressumUrl: proposal.proposedImpressumUrl || undefined,
        proposedContactPerson: proposal.proposedContactPerson || undefined,
        proposedEmail: proposal.proposedEmail || undefined,
        proposedPhone: proposal.proposedPhone || undefined,
        proposedStravaClubUrl: proposal.proposedStravaClubUrl || undefined,
        proposedInstagramUrl: proposal.proposedInstagramUrl || undefined,
        proposedFacebookUrl: proposal.proposedFacebookUrl || undefined,
        proposedKomootUrl: proposal.proposedKomootUrl || undefined,
        submitterName: proposal.submitterName || undefined,
        submitterEmail: proposal.submitterEmail || undefined,
        note: proposal.note || undefined,
      });
      toast.success('Vorschlag eingereicht — vielen Dank!');
      onClose();
    } catch {
      toast.error('Fehler beim Einreichen');
    } finally {
      setSubmitting(false);
    }
  };

  if (!show) return null;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 50, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ width: '100%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto', overscrollBehavior: 'contain',
        background: 'var(--surface-bg)', borderRadius: '20px 20px 0 0', padding: '24px 20px 32px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>Änderung vorschlagen</h2>
          <button onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 20 }}>✕</button>
        </div>
        <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 20 }}>
          Fülle nur die Felder aus, die du ändern oder ergänzen möchtest. Kontaktdaten (Ansprechpartner, E-Mail, Telefon) werden nicht öffentlich angezeigt.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>Name</label>
            <input value={proposal.proposedName} onChange={e => setProposal(p => ({ ...p, proposedName: e.target.value }))}
              style={inputStyle} placeholder={organizer.name} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>Webseite</label>
            <input value={proposal.proposedWebsite} onChange={e => setProposal(p => ({ ...p, proposedWebsite: e.target.value }))}
              style={inputStyle} placeholder="https://..." />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>Logo-URL</label>
            <input value={proposal.proposedLogoUrl} onChange={e => setProposal(p => ({ ...p, proposedLogoUrl: e.target.value }))}
              style={inputStyle} placeholder="https://..." />
            {proposal.proposedLogoUrl && (
              <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
                <img src={proposal.proposedLogoUrl} alt="Vorschau"
                  style={{ width: 48, height: 48, borderRadius: '50%', objectFit: 'contain',
                    border: '1px solid var(--surface-border)', background: 'white', padding: 3 }}
                  onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Vorschau</span>
              </div>
            )}
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>Straße</label>
            <input value={proposal.proposedStreet} onChange={e => setProposal(p => ({ ...p, proposedStreet: e.target.value }))}
              style={inputStyle} placeholder="Musterstraße 12" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 8 }}>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>PLZ</label>
              <input value={proposal.proposedPostalCode} onChange={e => setProposal(p => ({ ...p, proposedPostalCode: e.target.value }))}
                style={inputStyle} placeholder="12345" />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>Ort</label>
              <input value={proposal.proposedCity} onChange={e => setProposal(p => ({ ...p, proposedCity: e.target.value }))}
                style={inputStyle} placeholder="Musterstadt" />
            </div>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>(Bundes)Land</label>
            <select value={proposal.proposedBundesland} onChange={e => setProposal(p => ({ ...p, proposedBundesland: e.target.value }))}
              style={inputStyle}>
              <option value="">— Bundesland wählen —</option>
              <BundeslandOptions />
            </select>
          </div>
          <div style={{ borderTop: '1px solid var(--surface-border)', paddingTop: 12, marginTop: 4 }}>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>Social Media</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>Strava (Club)</label>
                <input value={proposal.proposedStravaClubUrl} onChange={e => setProposal(p => ({ ...p, proposedStravaClubUrl: e.target.value }))}
                  style={inputStyle} placeholder="https://www.strava.com/clubs/..." />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>Instagram</label>
                <input value={proposal.proposedInstagramUrl} onChange={e => setProposal(p => ({ ...p, proposedInstagramUrl: e.target.value }))}
                  style={inputStyle} placeholder="https://www.instagram.com/..." />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>Facebook</label>
                <input value={proposal.proposedFacebookUrl} onChange={e => setProposal(p => ({ ...p, proposedFacebookUrl: e.target.value }))}
                  style={inputStyle} placeholder="https://www.facebook.com/..." />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>Komoot</label>
                <input value={proposal.proposedKomootUrl} onChange={e => setProposal(p => ({ ...p, proposedKomootUrl: e.target.value }))}
                  style={inputStyle} placeholder="https://www.komoot.com/user/..." />
              </div>
            </div>
          </div>
          <div style={{ borderTop: '1px solid var(--surface-border)', paddingTop: 12, marginTop: 4 }}>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
              Kontaktdaten (werden nicht öffentlich angezeigt)
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>Ansprechpartner</label>
                <input value={proposal.proposedContactPerson} onChange={e => setProposal(p => ({ ...p, proposedContactPerson: e.target.value }))}
                  style={inputStyle} placeholder="Name" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>E-Mail</label>
                  <input type="email" value={proposal.proposedEmail} onChange={e => setProposal(p => ({ ...p, proposedEmail: e.target.value }))}
                    style={inputStyle} placeholder="info@..." />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>Telefon</label>
                  <input type="tel" value={proposal.proposedPhone} onChange={e => setProposal(p => ({ ...p, proposedPhone: e.target.value }))}
                    style={inputStyle} placeholder="+49..." />
                </div>
              </div>
            </div>
          </div>
          <div style={{ borderTop: '1px solid var(--surface-border)', paddingTop: 12, marginTop: 4 }}>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>Deine Angaben (optional)</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>Dein Name</label>
                <input value={proposal.submitterName} onChange={e => setProposal(p => ({ ...p, submitterName: e.target.value }))}
                  style={inputStyle} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>Deine E-Mail</label>
                <input type="email" value={proposal.submitterEmail} onChange={e => setProposal(p => ({ ...p, submitterEmail: e.target.value }))}
                  style={inputStyle} />
              </div>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>Anmerkung</label>
              <textarea value={proposal.note} onChange={e => setProposal(p => ({ ...p, note: e.target.value }))}
                rows={3} style={{ ...inputStyle, resize: 'vertical' }} placeholder="Hinweise zum Vorschlag..." />
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
          <button onClick={onClose}
            style={{ flex: 1, padding: '12px', borderRadius: 10, background: 'none',
              border: '1px solid var(--surface-border)', color: 'var(--text-secondary)', fontSize: 14, cursor: 'pointer' }}>
            Abbrechen
          </button>
          <button onClick={submit} disabled={submitting}
            style={{ flex: 2, padding: '12px', borderRadius: 10, background: 'var(--accent-warm)',
              border: 'none', color: 'white', fontSize: 14, fontWeight: 600, cursor: 'pointer', opacity: submitting ? 0.6 : 1 }}>
            {submitting ? 'Wird eingereicht...' : 'Vorschlag einreichen'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrganizerProposeDialog;
